import {
  CellEditRequestEvent,
  CellMouseOutEvent,
  CellMouseOverEvent,
  CellStyle,
  ColDef,
  GetRowIdParams,
  GridReadyEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRecoilValue } from "recoil";
import {
  FlatProduct,
  flatTabsFamily,
  lineSheetSetFamily,
  orderSheetHasDiscountConditionFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import ProductImageCell, {
  PRODUCT_IMAGE_SIZE,
} from "@/features/order-sheet-sets/product-image-cell";
import PriceAdjustedByCell from "@/features/order-sheet-sets/price-adjusted-by-cell";
import NumericEditor from "@/features/ui/numeric-editor";
import { produce } from "immer";
import { times, uniq } from "lodash";
import { Box } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import ProductCheckboxCell from "@/features/order-sheet-sets/product-checkbox-cell";
import SheetHeader from "@/features/order-sheet-sets/sheet-header";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SheetCheckBoxHeader from "@/features/order-sheet-sets/sheet-checkbox-header";
import ProductImageHeader from "@/features/order-sheet-sets/product-image-header";
import { MAX_PRODUCT_IMAGE_COUNT } from "@/features/order-sheet-sets/representative-product-image";
import OverlappedConditionTypeCell from "../ui/overlapped-condition-type-cell";
import OverlappedValueCell from "../ui/overlapped-value-cell";
import TakeAllProductsCell from "../ui/take-all-products-cell";
import OrderSheetIvaCell from "@/features/order-sheet-sets/order-sheet-iva-cell";
import useIdentity from "@/features/ui/hooks/use-identity";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import HighlightCellRenderer from "@/shared/components/ag-grid/highlight-cell-renderer";

interface LineSheetTabProps {
  lineSheetSetId: number;
  lineSheetId: number;
  tabIndex: number;
  isLoading: boolean;
  onSelectedProductIdsChange: (ids: number[]) => void;
  onGridReady: (event: GridReadyEvent<FlatProduct>) => void;
  mutate?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function getRowId(params: GetRowIdParams<FlatProduct>) {
  return params.data.rowId;
}

function LineSheetTab({
  lineSheetSetId,
  lineSheetId,
  tabIndex,
  isLoading,
  onSelectedProductIdsChange,
  onGridReady,
  mutate,
  onDirtyChange,
}: LineSheetTabProps) {
  const identity = useIdentity();
  const isAgency = identity?.company?.type === "AGENCY";
  const { getInflatedProductById, updateInflatedProduct } = useInflatedTabs();
  const { warning: showWarning } = useAppToasts();

  const lineSheetSetKey = useMemo(() => {
    return {
      lineSheetSetId,
    };
  }, [lineSheetSetId]);
  const lineSheetSetState = useRecoilValue(lineSheetSetFamily(lineSheetSetKey));
  const sheetKey = useMemo(() => {
    return {
      lineSheetSetId,
      lineSheetId,
    };
  }, [lineSheetSetId, lineSheetId]);

  const flatSheetState = useRecoilValue(flatTabsFamily(sheetKey));
  // DISCOUNT 컨디션 상품이 하나도 없으면 IVA 컬럼은 빈 칸만 남아서 통째로 감춘다.
  const hasDiscountCondition = useRecoilValue(
    orderSheetHasDiscountConditionFamily({ key: sheetKey, tabIndex })
  );

  const rowData = useMemo(() => {
    if (flatSheetState && tabIndex > -1) {
      return flatSheetState.tabs
        .find((tab) => tab.index === tabIndex)
        ?.products.filter((product) => product.isFiltered);
    }
  }, [flatSheetState, tabIndex]);

  const { t, tTitle, tCurrencyString } = useI18nHelper();
  const isPublishedRef = useRef<boolean>(!!lineSheetSetState?.lineSheetSet?.publishedAt);

  useEffect(() => {
    isPublishedRef.current = !!lineSheetSetState?.lineSheetSet?.publishedAt;
    gridRef.current?.api?.refreshCells({ force: true, columns: ["brandSKUEtc"] });
  }, [lineSheetSetState]);

  const gridRef = useRef<AgGridReact<FlatProduct> | null>(null);
  const [isGridRefSet, setIsGridRefSet] = useState(false);

  const handleSetGridRef = useCallback(
    (ref: AgGridReact<FlatProduct> | null) => {
      gridRef.current = ref;
      if (ref && !isGridRefSet) {
        setIsGridRefSet(true);
      }
    },
    [isGridRefSet, setIsGridRefSet]
  );

  const [isImagesCollapsed, setIsImagesCollapsed] = useState<boolean>(true);

  const components = useMemo<{ [p: string]: any }>(() => {
    return {
      agColumnHeader: SheetHeader,
    };
  }, []);

  const [colDefs, setColDefs] = useState<ColDef<FlatProduct>[]>([
    {
      cellRenderer: ProductCheckboxCell,
      rowSpan: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex % 3 === 0 ? 3 : 1;
        }
        return 1;
      },
      width: 40,
      pinned: "left",
      field: "isChecked",
      headerName: "",
      resizable: false,
      suppressMovable: true,
      headerCheckboxSelection: false,
      showDisabledCheckboxes: false,
      headerComponent: SheetCheckBoxHeader,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 === 0,
        "product-even-row": (params) => params.rowIndex % 6 === 3,
        "product-checkbox-cell": (params) => params.rowIndex % 3 === 0,
      },
      headerClass: "product-checkbox-header",
    },
    {
      valueGetter: (params) => {
        if (params.data) {
          return {
            imageUrls: params.data.imageUrls?.map((imageUrl) => {
              return {
                url: imageUrl.url,
                type: imageUrl.type,
              };
            }),
            id: params.data?.id,
            searchWord: params.data?.searchWord,
            sku: params.data?.sku,
            color: params.data?.color,
            brandName: params.data?.brandName,
            aiImageChecked: params.data?.aiImageChecked,
          };
        }
        return null;
      },
      cellStyle: { fontSize: "12px" },
      cellRenderer: ProductImageCell,
      width: PRODUCT_IMAGE_SIZE,
      autoHeight: false,
      rowSpan: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex % 3 === 0 ? 3 : 1;
        }
        return 1;
      },
      resizable: false,
      pinned: "left",
      field: "i",
      // headerName: "",
      headerClass: "product-image-header",
      headerComponent: ProductImageHeader,
      headerComponentParams: {
        isCollapsed: isImagesCollapsed,
        onClick: (isCollapse: boolean) => {
          setIsImagesCollapsed(isCollapse);
        },
      },
      cellRendererParams: {
        isCollapsed: isImagesCollapsed,
        isLineSheet: true,
        mutate: mutate,
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 === 0,
        "product-even-row": (params) => params.rowIndex % 6 === 3,
      },
    },
    {
      field: "brandSKUEtc",
      autoHeaderHeight: true,
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["brand", "sku", "etc_info"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        // SKU 미연결: 의도된 skip이면 회색(sku-skipped), 아니면 빨강(sku-no-match).
        "sku-no-match": (params) =>
          isPublishedRef.current &&
          params.rowIndex % 3 === 1 &&
          !params.data?.lineSheetProductSkuId &&
          !params.data?.isSkuSkipped,
        "sku-skipped": (params) =>
          isPublishedRef.current &&
          params.rowIndex % 3 === 1 &&
          !params.data?.lineSheetProductSkuId &&
          !!params.data?.isSkuSkipped,
        "brand-no-match": (params) => isPublishedRef.current && params.rowIndex % 3 === 0 && !params.data?.brandId,
      },
    },
    {
      field: "genderCategorySubcategory",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["gender", "category", "subcategory"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "colorCodeColorNameMaterial",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["season", "color", "material"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "priceAmount",
      cellStyle: (params): CellStyle => {
        const cellStyle: CellStyle = {
          textAlign: "end",
          fontSize: "11px",
          fontWeight: "400",
          color: "#54565F",
          padding: "0",
        };
        const rowIndex = params.node?.rowIndex;
        if (typeof rowIndex === "number" && rowIndex % 3 === 0) {
          cellStyle.color = "#303136";
          cellStyle.fontSize = "12px";
          cellStyle.fontWeight = "700";
        }
        return cellStyle;
      },
      width: 96,
      resizable: false,
      valueFormatter: (params) => {
        if (!params.value) {
          return "-";
        }
        if (params.data?.priceCurrency) {
          // 개별 가격 정보에 통화 정보가 있으면 해당 통화로 표시
          return tCurrencyString(params.value, {
            type: "SYMBOL",
            code: params.data.priceCurrency,
          });
        } else if (lineSheetSetState?.lineSheetSet?.currency) {
          // 개별 가격 정보가 없지만 라인시트 세트에 통화 정보가 있으면 해당 통화로 표시
          return tCurrencyString(params.value, {
            type: "SYMBOL",
            code: lineSheetSetState?.lineSheetSet?.currency,
          });
        } else {
          // 통화 정보가 없으면 기본값 사용
          return tCurrencyString(params.value);
        }
      },
      pinned: "left",
      headerComponentParams: {
        items: ["supply_price", "cost", "retail"],
      },
      headerClass: "header-right", //우측 정렬
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        "product-price-cell": (params) => params.rowIndex % 3 === 0,
      },
    },
    {
      headerName: "IVA",
      colId: "ivaCondition",
      pinned: "left",
      width: 100,
      resizable: false,
      editable: false,
      cellStyle: {
        overflow: "visible",
        padding: "1px",
        paddingLeft: "4px",
      },
      cellRenderer: OrderSheetIvaCell,
      valueGetter: (params) => {
        const moldIndex =
          params.node != null && params.node.rowIndex != null
            ? params.node.rowIndex % 3
            : -1;
        if (moldIndex === 0) return params.data?.applyIva;
        if (moldIndex === 1) return params.data?.ivaValue;
        return null;
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "priceAdjustedByPartial",
      headerName: "",
      pinned: "left",
      width: 112,
      resizable: false,
      colId: "priceAdjustedByPartial",
      cellRenderer: PriceAdjustedByCell,
      cellStyle: {
        display: "flex",
        overflow: "visible",
        padding: "1px",
        paddingLeft: "16px",
      },
      editable: (params) => {
        const moldIndex = params.node.rowIndex != null ? params.node.rowIndex % 3 : -1;
        return isAgency && moldIndex === 1;
      },
      valueGetter: (params) => {
        //cell re render가 되려면 이 값이 변경되어함,
        //custom cell 에서 value로 접근 가능
        if (params.node && params.node.rowIndex !== null) {
          const rowIndex = params.node.rowIndex;
          const partialIndex = rowIndex % 3;
          if (partialIndex === 0) {
            // const nextRowData = {
            //   by: params.data?.priceAdjustedByPartial,
            //   value: params.api.getDisplayedRowAtIndex(rowIndex + 1)?.data
            //     ?.priceAdjustedByPartial,
            // };
            return params.data?.priceAdjustedByPartial;
          } else if (partialIndex === 1) {
            return params.data?.priceAdjustedByPartial;
            // return {
            //   by: params.api.getDisplayedRowAtIndex(rowIndex - 1)?.data
            //     ?.priceAdjustedByPartial,
            //   value: params.data?.priceAdjustedByPartial,
            // };
          } else {
            return params.data?.sizes;
          }
        }
        return params.data?.priceAdjustedByPartial;
      },
      cellRendererSelector: (params) => {
        const rowIndex = params.node.rowIndex;
        if (rowIndex != null) {
          const index = rowIndex % 3;
          if (index === 0) {
            return {
              component: OverlappedConditionTypeCell,
            };
          } else if (index === 1) {
            return {
              component: OverlappedValueCell,
              params: {
                unit: "%",
              },
            };
          } else if (index === 2) {
            return {
              component: TakeAllProductsCell,
            };
          }
        }

        return undefined;
      },
      cellEditor: forwardRef(NumericEditor),
      cellEditorParams: {
        unit: "%",
        tabIndex,
        sheetKey,
        isEditable: isAgency,
      },
      cellClass: "product-price-adjusted-cell",
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        "product-price-adjusted-selectbox-cell": (params) =>
          params.rowIndex % 3 === 0,
        "product-price-adjusted-input-cell": (params) =>
          params.rowIndex % 3 === 1,
        "product-editable-cell": (params) => isAgency && params.rowIndex % 3 === 1,
      },
    },
  ]);

  const maxProductImageCount = useMemo(() => {
    return (
      rowData
        ?.map((i) =>
          Math.min(MAX_PRODUCT_IMAGE_COUNT, (i.imageUrls || []).length)
        )
        .reduce((acc, cur) => Math.max(acc, cur), 0) || 0
    );
  }, [rowData]);

  useEffect(() => {
    // gridRef.current?.api.refreshHeader();
    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex((colDef) => colDef.field === "i");

        let width;
        if (isImagesCollapsed) {
          width = PRODUCT_IMAGE_SIZE;
        } else {
          width =
            PRODUCT_IMAGE_SIZE * (maxProductImageCount + (isAgency ? 1 : 0)) +
            (maxProductImageCount - 1) * 4 +
            2;
        }

        if (i > -1) {
          draft[i] = {
            ...draft[i],
            width,
            headerComponentParams: {
              ...draft[i].headerComponentParams,
              isCollapsed: isImagesCollapsed,
              isDisabled: false,
            },
            cellRendererParams: {
              ...draft[i].cellRendererParams,
              isCollapsed: isImagesCollapsed,
            },
          };
        }
      });
    });
  }, [isAgency, isImagesCollapsed, maxProductImageCount]);

  useEffect(() => {
    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex((c) => c.field === "priceAdjustedByPartial");
        if (i > -1) {
          draft[i].cellRendererParams = {
            tabIndex,
            sheetKey,
            isEditable: isAgency,
          };
          draft[i].editable = (params) => {
            const moldIndex = params.node.rowIndex != null ? params.node.rowIndex % 3 : -1;
            return isAgency && moldIndex === 1;
          };
          draft[i].cellClassRules!["product-editable-cell"] = (params) =>
            isAgency && params.rowIndex % 3 === 1;
        }
        const ivaIdx = draft.findIndex((c) => c.colId === "ivaCondition");
        if (ivaIdx > -1) {
          draft[ivaIdx].hide = !hasDiscountCondition;
          draft[ivaIdx].cellRendererParams = {
            tabIndex,
            sheetKey,
            isEditable: isAgency,
            onUpdatingInflatedProductComplete: onDirtyChange,
          };
        }
      });
    });
  }, [tabIndex, sheetKey, isAgency, hasDiscountCondition, onDirtyChange]);

  const sizeLength = useMemo(() => {
    if (rowData) {
      return rowData
        .map((i) => {
          return i.sizes ? i.sizes.length : 0;
        })
        .reduce((acc, cur) => {
          return Math.max(acc, cur);
        }, 0);
    }
    return 0;
  }, [rowData]);

  useEffect(() => {
    if (sizeLength > 0) {
      setColDefs((prev) => {
        const i = prev.findLastIndex(
          (p) => p.field === "priceAdjustedByPartial"
        );
        if (i > -1) {
          return [
            ...prev.slice(0, i + 1),
            ...((): ColDef<FlatProduct>[] => {
              return times(sizeLength, (index): ColDef<FlatProduct> => {
                return {
                  headerName: `sq${index}`,
                  headerClass: "product-size-or-quantity-cell-header",
                  colId: `sq_${index}`,
                  width: 80,
                  cellStyle: (params) => {
                    return {
                      textAlign: "center",
                      fontSize: "12px",
                    };
                  },
                  valueGetter: (params) => params.data?.sizes?.[index],
                  valueFormatter: (params) => {
                    if (params.value === -1) {
                      return "∞";
                    }
                    if (params.value === 0) {
                      // 재고(가운데) 행의 0 = 가용재고 차감 결과 품절 — 오더시트 그리드와 동일 표기.
                      const rowIndex = params.node?.rowIndex;
                      return rowIndex != null && rowIndex % 3 === 1
                        ? t("sold_out")
                        : "";
                    }
                    return params.value;
                  },
                  valueSetter: (params) => {
                    return true;
                  },
                  cellClassRules: {
                    "product-odd-row": (params) => params.rowIndex % 6 < 3,
                    "product-even-row": (params) => params.rowIndex % 6 >= 3,
                    "product-each-last-row": (params) =>
                      params.rowIndex % 3 === 2,
                  },
                  editable: false,
                  resizable: true,
                  headerComponentParams: {
                    items: ["size", "stock", "quantity.abbr"],
                  },
                };
              });
            })(),
          ];
        }
        return prev;
      });
    }
  }, [t, tabIndex, sizeLength]);

  const defaultColDef = useMemo((): ColDef<FlatProduct> => {
    return {
      autoHeight: false,
      cellStyle: { fontSize: "12px", padding: "0" },
    };
  }, []);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<FlatProduct>) => {
      onGridReady(event);
    },
    [onGridReady]
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<FlatProduct>) => {
      onSelectedProductIdsChange(
        uniq(event.api.getSelectedRows().map((row) => row.id))
      );
      event.api.refreshCells({
        force: true,
        columns: ["isChecked"],
      });
    },
    [onSelectedProductIdsChange]
  );

  const handleMouseOver = useCallback(
    (event: CellMouseOverEvent<FlatProduct>) => {
      const rowIndex = event.node.rowIndex;
      if (rowIndex !== null && rowIndex % 3 === 0) {
        if (
          event.colDef.headerClass &&
          (event.colDef.headerClass
            .toString()
            .includes("product-checkbox-header") ||
            event.colDef.headerClass
              .toString()
              .includes("product-image-header"))
        ) {
          const agPinnedLeftColsElement = document.querySelector(
            ".ag-pinned-left-cols-container"
          );
          if (agPinnedLeftColsElement !== null) {
            const secondRowElement = agPinnedLeftColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.add("ag-row-hover");
            thirdRowElement?.classList.add("ag-row-hover");
          }
          const agCenterColsElement = document.querySelector(
            ".ag-center-cols-container"
          );
          if (agCenterColsElement !== null) {
            const secondRowElement = agCenterColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.add("ag-row-hover");
            thirdRowElement?.classList.add("ag-row-hover");
          }
        }
      }
    },
    []
  );

  const onCellEditRequest = useCallback(
    (event: CellEditRequestEvent<FlatProduct>) => {
      const data = event.data;
      const colId = event.column.getColId();
      const rowIndex = event.rowIndex === null ? -1 : event.rowIndex;
      const moldIndex = rowIndex % 3;

      if (colId !== "priceAdjustedByPartial") return;

      const inflatedProduct = getInflatedProductById(sheetKey, tabIndex, data.id);
      if (!inflatedProduct) return;

      if (moldIndex === 0) {
        const nextData = produce(inflatedProduct, (draft) => {
          draft.priceAdjustedBy.by = event.newValue;
        });
        updateInflatedProduct(sheetKey, tabIndex, nextData);
        onDirtyChange?.(true);
      } else if (moldIndex === 1) {
        let newValue = event.newValue;
        if (isNaN(newValue) || newValue === null || newValue === undefined) {
          newValue = 0;
        }
        if (newValue > 100 || newValue < -100) {
          showWarning(
            t("common:apply_price_condition_form.messages.error.out_of_range"),
            "apply_price_condition_form"
          );
          return;
        }
        const nextData = produce(inflatedProduct, (draft) => {
          draft.priceAdjustedBy.value = newValue;
        });
        updateInflatedProduct(sheetKey, tabIndex, nextData);
        onDirtyChange?.(true);
      }
    },
    [sheetKey, tabIndex, getInflatedProductById, updateInflatedProduct, showWarning, t, onDirtyChange]
  );

  const handleMouseOut = useCallback(
    (event: CellMouseOutEvent<FlatProduct>) => {
      const rowIndex = event.node.rowIndex;
      if (rowIndex !== null && rowIndex % 3 === 0) {
        if (
          event.colDef.headerClass &&
          (event.colDef.headerClass
            .toString()
            .includes("product-checkbox-header") ||
            event.colDef.headerClass
              .toString()
              .includes("product-image-header"))
        ) {
          const agPinnedLeftColsElement = document.querySelector(
            ".ag-pinned-left-cols-container"
          );
          if (agPinnedLeftColsElement !== null) {
            const secondRowElement = agPinnedLeftColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.remove("ag-row-hover");
            thirdRowElement?.classList.remove("ag-row-hover");
          }
          const agCenterColsElement = document.querySelector(
            ".ag-center-cols-container"
          );
          if (agCenterColsElement !== null) {
            const secondRowElement = agCenterColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.remove("ag-row-hover");
            thirdRowElement?.classList.remove("ag-row-hover");
          }
        }
      }
    },
    []
  );

  return (
    <Box
      className={"ag-theme-alpine"}
      paddingBottom={"16px"}
      height={"100%"}
      width={"100%"}
    >
      <AgGridReact<FlatProduct>
        ref={handleSetGridRef}
        suppressRowTransform={true}
        readOnlyEdit={true}
        getRowId={getRowId}
        headerHeight={24}
        columnDefs={colDefs}
        rowData={rowData}
        //왜 25를 해야 24가 되는거야?;;
        rowHeight={25}
        rowModelType={"clientSide"}
        defaultColDef={defaultColDef}
        onGridReady={handleGridReady}
        rowSelection={"multiple"}
        suppressRowClickSelection={true}
        onSelectionChanged={handleSelectionChanged}
        onCellMouseOver={handleMouseOver}
        onCellMouseOut={handleMouseOut}
        onCellEditRequest={isAgency ? onCellEditRequest : undefined}
        enableCellTextSelection={true}
        components={components}
        suppressRowHoverHighlight={false}
        columnHoverHighlight={true}
        suppressMovableColumns={true}
        animateRows={false}
        rowBuffer={10}
        suppressRowVirtualisation={false}
        suppressColumnVirtualisation={false}
        suppressDragLeaveHidesColumns={true}
      />
    </Box>
  );
}

export default LineSheetTab;
